import React from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  Paper,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import GppGoodIcon from '@mui/icons-material/GppGood';
import { LandingNavbar } from '../components/LandingNavbar';
import Footer from '../components/Footer';

const brand = {
  primary: '#800b33',
  primaryDark: '#5c0620',
  darkText: '#1e293b',
  muted: '#64748b',
  surface: '#ffffff',
  bg: '#f8fafc',
  accent: '#fdf2f8',
};

// --- Privacy Page Content ---

// --- Privacy Page Content ---

const PolicySection = ({ title, children, icon }) => (
  <Box sx={{ mb: 6 }}>
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <Box sx={{ p: 1, borderRadius: 2, bgcolor: brand.accent, color: brand.primary }}>
        {icon || <GppGoodIcon />}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: brand.darkText }}>
        {title}
      </Typography>
    </Stack>
    <Box sx={{ pl: { xs: 0, md: 7 } }}>
      {children}
    </Box>
  </Box>
);

const PrivacyPolicy = () => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: brand.bg }}>
      <LandingNavbar />

      {/* Header Region */}
      <Box sx={{
        py: 10,
        background: 'radial-gradient(circle at 50% -20%, rgba(128,11,51,0.08), transparent 70%)',
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: brand.primary, fontWeight: 700, letterSpacing: 2 }}>
            LEGAL & COMPLIANCE
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: 800, color: brand.darkText, mt: 2, mb: 3, letterSpacing: '-0.03em' }}>
            Privacy Policy
          </Typography>
          <Typography variant="body1" sx={{ color: brand.muted, fontSize: '1.2rem', maxWidth: 600, mx: 'auto' }}>
            Your community's trust is our currency. Here is how we protect your financial data.
          </Typography>
        </Container>
      </Box>

      {/* Content Card */}
      <Container maxWidth="md" sx={{ mb: 12, position: 'relative', top: -40 }}>
        <Paper elevation={0} sx={{
          p: { xs: 4, md: 8 },
          borderRadius: 4,
          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.05)',
          border: '1px solid rgba(0,0,0,0.04)'
        }}>
          <Typography variant="body1" paragraph sx={{ color: brand.darkText, mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
            Effective Date: January 1, 2026
          </Typography>

          <Divider sx={{ mb: 6 }} />

          <PolicySection title="Data Collection & Usage" icon={<SecurityIcon />}>
            <Typography variant="body1" sx={{ color: brand.muted, lineHeight: 1.8, mb: 2 }}>
              We collect only the essential information required to facilitate your group's financial operations. This includes member names, contact details, and transaction records (contributions, loans, and penalties).
            </Typography>
            <Typography variant="body1" sx={{ color: brand.muted, lineHeight: 1.8 }}>
              We do not sell your data to third parties. Your data is used strictly for:
            </Typography>
            <List dense>
              {['Providing the Savvy service', 'Generating financial reports', 'Sending transaction notifications', 'Fraud prevention'].map(item => (
                <ListItem key={item}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleIcon sx={{ fontSize: 18, color: brand.primary }} /></ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ color: brand.muted }} />
                </ListItem>
              ))}
            </List>
          </PolicySection>

          <PolicySection title="Data Security" icon={<LockIcon />}>
            <Typography variant="body1" sx={{ color: brand.muted, lineHeight: 1.8 }}>
              Security is not an afterthought; it is our foundation. We employ bank-grade security measures to ensure your community's wealth is protected.
            </Typography>
            <Paper variant="outlined" sx={{ mt: 3, p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: brand.darkText }}>Encryption Everywhere</Typography>
                  <Typography variant="body2" sx={{ color: brand.muted }}>All data is encrypted in transit using TLS 1.3 and at rest using AES-256 standards.</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: brand.darkText }}>Access Controls</Typography>
                  <Typography variant="body2" sx={{ color: brand.muted }}>Strict role-based access limits data exposure. Only authorized group admins can view sensitive financial details.</Typography>
                </Box>
              </Stack>
            </Paper>
          </PolicySection>

          <PolicySection title="Data Retention">
            <Typography variant="body1" sx={{ color: brand.muted, lineHeight: 1.8 }}>
              We retain your group's data for as long as your account is active. Upon closure, you have the right to request a full export of your financial history. After a grace period of 90 days, inactive closed account data is permanently purged from our systems.
            </Typography>
          </PolicySection>

          <Divider sx={{ my: 6 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, color: brand.darkText, mb: 1 }}>
            Contact Our Data Protection Officer
          </Typography>
          <Typography variant="body1" sx={{ color: brand.muted }}>
            If you have any questions about this policy, please contact us at <Link href="mailto:privacy-savvy@savvy-community.com" sx={{ color: brand.primary, fontWeight: 600, textDecoration: 'none' }}>privacy-savvy@savvy-community.com</Link>.
          </Typography>

        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default PrivacyPolicy;
